import { getBackendUrl } from '@/lib/backendUrl';
import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { ScrollArea } from './ui/scroll-area';
import { AlertTriangle, EyeOff, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = getBackendUrl();

/**
 * UnpublishJobsDialog
 *
 * Props:
 *   open          bool
 *   onOpenChange  fn(bool)
 *   month         int  1-12
 *   year          int
 *   jobs          array of job objects (from current schedule view)
 *   sites         array  [{id, name}]
 *   auditors      array  [{id, first_name, last_name}]
 *   clients       array  [{id, name}]
 *   onSuccess     fn(result)
 */
export default function UnpublishJobsDialog({
  open,
  onOpenChange,
  month,
  year,
  jobs = [],
  sites = [],
  auditors = [],
  clients = [],
  onSuccess,
}) {
  const [mode, setMode] = useState('whole_month'); // 'whole_month' | 'individual'
  const [selectedJobIds, setSelectedJobIds] = useState([]);
  const [filterAuditorId, setFilterAuditorId] = useState('__all__');
  const [filterSiteId, setFilterSiteId] = useState('__all__');
  const [filterClientId, setFilterClientId] = useState('__all__');
  const [preview, setPreview] = useState(null);   // dry-run result
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const reset = () => {
    setMode('whole_month');
    setSelectedJobIds([]);
    setFilterAuditorId('__all__');
    setFilterSiteId('__all__');
    setFilterClientId('__all__');
    setPreview(null);
    setConfirmed(false);
    setError('');
  };

  const handleClose = () => {
    reset();
    onOpenChange(false);
  };

  // Compute displayable jobs (published jobs in the current month, filtered)
  const publishedJobs = jobs.filter(j => j.batch_published_at || j.is_published);

  const filteredJobs = publishedJobs.filter(j => {
    if (filterAuditorId !== '__all__' && String(j.auditor_id) !== String(filterAuditorId) &&
        !(j.auditors || []).some(a => String(a.id) === String(filterAuditorId))) return false;
    if (filterSiteId !== '__all__' && String(j.site_id) !== String(filterSiteId)) return false;
    if (filterClientId !== '__all__' && String(j.client_id) !== String(filterClientId)) return false;
    return true;
  });

  const toggleJob = useCallback((jobId) => {
    setSelectedJobIds(prev =>
      prev.includes(jobId) ? prev.filter(id => id !== jobId) : [...prev, jobId]
    );
  }, []);

  const toggleAll = () => {
    if (selectedJobIds.length === filteredJobs.length) {
      setSelectedJobIds([]);
    } else {
      setSelectedJobIds(filteredJobs.map(j => j.id));
    }
  };

  const buildPayload = (dryRun) => ({
    mode,
    month,
    year,
    job_ids: mode === 'individual' ? selectedJobIds : null,
    filter_auditor_id: filterAuditorId !== '__all__' ? parseInt(filterAuditorId) : null,
    filter_site_id: filterSiteId !== '__all__' ? parseInt(filterSiteId) : null,
    filter_client_id: filterClientId !== '__all__' ? parseInt(filterClientId) : null,
    dry_run: dryRun,
  });

  const authHeaders = () => {
    const token = localStorage.getItem('access_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const runDryRun = async () => {
    if (mode === 'individual' && selectedJobIds.length === 0) {
      setError('Select at least one job to unpublish.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post(
        `${API}/api/batch-schedule/jobs/unpublish`,
        buildPayload(true),
        { headers: authHeaders() },
      );
      setPreview(data);
      setConfirmed(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to preview unpublish.');
    } finally {
      setLoading(false);
    }
  };

  const runUnpublish = async () => {
    if (!confirmed) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post(
        `${API}/api/batch-schedule/jobs/unpublish`,
        buildPayload(false),
        { headers: authHeaders() },
      );
      onSuccess?.(data);
      handleClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Unpublish failed.');
    } finally {
      setLoading(false);
    }
  };

  const monthLabel = new Date(year, month - 1, 1).toLocaleString('default', {
    month: 'long',
    year: 'numeric',
  });

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <EyeOff className="h-5 w-5 text-amber-500" />
            Unpublish Jobs — {monthLabel}
          </DialogTitle>
          <DialogDescription>
            Revert published jobs to approved-draft state (hidden from auditors).
            Only affects jobs in published batches.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Mode */}
          <div className="flex gap-2">
            <Button
              variant={mode === 'whole_month' ? 'default' : 'outline'}
              size="sm"
              data-testid="unpublish-mode-whole-month"
              onClick={() => { setMode('whole_month'); setPreview(null); }}
            >
              Whole Month
            </Button>
            <Button
              variant={mode === 'individual' ? 'default' : 'outline'}
              size="sm"
              data-testid="unpublish-mode-individual"
              onClick={() => { setMode('individual'); setPreview(null); }}
            >
              Individual Jobs
            </Button>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Auditor</label>
              <Select value={filterAuditorId} onValueChange={v => { setFilterAuditorId(v); setPreview(null); }}>
                <SelectTrigger data-testid="unpublish-filter-auditor" className="h-8 text-xs">
                  <SelectValue placeholder="All auditors" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All auditors</SelectItem>
                  {auditors.map(a => (
                    <SelectItem key={a.id} value={String(a.id)}>
                      {a.first_name} {a.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Site</label>
              <Select value={filterSiteId} onValueChange={v => { setFilterSiteId(v); setPreview(null); }}>
                <SelectTrigger data-testid="unpublish-filter-site" className="h-8 text-xs">
                  <SelectValue placeholder="All sites" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All sites</SelectItem>
                  {sites.map(s => (
                    <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Client</label>
              <Select value={filterClientId} onValueChange={v => { setFilterClientId(v); setPreview(null); }}>
                <SelectTrigger data-testid="unpublish-filter-client" className="h-8 text-xs">
                  <SelectValue placeholder="All clients" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All clients</SelectItem>
                  {clients.map(c => (
                    <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Individual job selection */}
          {mode === 'individual' && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs text-muted-foreground">
                  Select jobs to unpublish ({selectedJobIds.length}/{filteredJobs.length} selected)
                </label>
                <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={toggleAll}>
                  {selectedJobIds.length === filteredJobs.length ? 'Deselect all' : 'Select all'}
                </Button>
              </div>
              <ScrollArea className="h-48 border rounded-md p-2">
                {filteredJobs.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-4">
                    No published jobs found for the current filters.
                  </p>
                ) : (
                  <div className="space-y-1" data-testid="unpublish-job-list">
                    {filteredJobs.map(job => (
                      <label
                        key={job.id}
                        className="flex items-center gap-2 cursor-pointer text-xs hover:bg-muted px-1 py-1 rounded"
                      >
                        <Checkbox
                          checked={selectedJobIds.includes(job.id)}
                          onCheckedChange={() => toggleJob(job.id)}
                          data-testid={`unpublish-job-checkbox-${job.id}`}
                        />
                        <span className="font-medium">#{job.id}</span>
                        <span className="text-muted-foreground">{job.site_name || job.site?.name}</span>
                        <span>{job.scheduled_date}</span>
                        <Badge variant="outline" className="text-[10px] h-4">published</Badge>
                      </label>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>
          )}

          {/* Dry-run preview */}
          {preview && (
            <div className="rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800 p-3 space-y-1">
              <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400 font-medium text-sm">
                <AlertTriangle className="h-4 w-4" />
                Preview — no changes made yet
              </div>
              <ul className="text-xs space-y-0.5 text-muted-foreground mt-1">
                <li data-testid="unpublish-preview-eligible">
                  Eligible jobs to unpublish: <strong>{preview.eligible_job_count}</strong> across{' '}
                  <strong>{preview.eligible_batch_count}</strong> batch(es)
                </li>
                {preview.already_unpublished_count > 0 && (
                  <li>Already in approved-draft: <strong>{preview.already_unpublished_count}</strong> batch(es) — skipped</li>
                )}
                {preview.ineligible_count > 0 && (
                  <li>Ineligible (not published): <strong>{preview.ineligible_count}</strong> job(s) — skipped</li>
                )}
              </ul>

              {preview.eligible_job_count > 0 && (
                <label className="flex items-center gap-2 mt-2 cursor-pointer text-sm">
                  <Checkbox
                    checked={confirmed}
                    onCheckedChange={setConfirmed}
                    data-testid="unpublish-confirm-checkbox"
                  />
                  I confirm: unpublish {preview.eligible_job_count} job(s) and hide them from auditors
                </label>
              )}
            </div>
          )}

          {error && (
            <p className="text-destructive text-sm" data-testid="unpublish-error">{error}</p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} data-testid="unpublish-cancel-btn">
            Cancel
          </Button>
          {!preview ? (
            <Button
              onClick={runDryRun}
              disabled={loading}
              data-testid="unpublish-preview-btn"
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <EyeOff className="h-4 w-4 mr-2" />}
              Preview
            </Button>
          ) : (
            <Button
              onClick={runUnpublish}
              disabled={loading || !confirmed || preview.eligible_job_count === 0}
              data-testid="unpublish-confirm-btn"
              variant="destructive"
            >
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <EyeOff className="h-4 w-4 mr-2" />}
              Unpublish {preview?.eligible_job_count ?? 0} Job(s)
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
